import { useEffect, useState } from "react";
import { CalendarClock, ExternalLink, Link2, Settings2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

/**
 * Book-the-kickoff CTA. Renders nothing if the calendar URL is missing (see
 * useOpsBriefingLink) so a rep never clicks a dead button.
 */
export function OpsBriefingBookButton({ link, size, className = "", variant }) {
  if (!link?.configured) return null;
  const subject = [link.calendarName, link.hostName && `with ${link.hostName}`]
    .filter(Boolean)
    .join(" ");
  // The label is a full sentence, so it wraps rather than overflowing the pill
  // on a phone — hence h-auto/whitespace-normal over the primitive's fixed height.
  return (
    <Button
      asChild
      size={size}
      variant={variant}
      className={`rounded-full w-full sm:w-auto h-auto min-h-10 py-2 whitespace-normal text-center leading-snug ${className}`}
      data-testid="ops-briefing-book-btn"
    >
      <a href={link.bookingUrl} target="_blank" rel="noopener noreferrer" title={subject}>
        <CalendarClock className="w-4 h-4 mr-2" />
        Click here to book your call with the ops team
        <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-60" />
      </a>
    </Button>
  );
}

/**
 * Admin-only editor for that URL. Lives wherever an admin will actually be when
 * ops hands over the link — the submissions list, and the completion screen
 * itself while the link is still missing.
 */
export function OpsBriefingLinkEditor({ link, compact = false }) {
  const [url, setUrl] = useState("");
  const [calendarName, setCalendarName] = useState("");
  const [hostName, setHostName] = useState("");
  const [open, setOpen] = useState(compact);

  // Seed the inputs once the settings arrive (and after a save re-reads them).
  useEffect(() => {
    setUrl(link?.bookingUrl || "");
    setCalendarName(link?.calendarName || "");
    setHostName(link?.hostName || "");
  }, [link?.bookingUrl, link?.calendarName, link?.hostName]);

  if (!link?.canEdit) return null;

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="rounded-full"
        onClick={() => setOpen(true)}
        data-testid="ops-briefing-edit-btn"
      >
        <Settings2 className="w-4 h-4 mr-2" />
        {link.configured ? "Edit booking link" : "Add booking link"}
      </Button>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border bg-secondary/20 p-3 text-left" data-testid="ops-briefing-editor">
      <div className="flex items-center gap-2 text-sm">
        <Link2 className="w-4 h-4 text-primary" />
        <span>Ops briefing calendar</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label className="text-xs text-muted-foreground">Booking URL</Label>
          <Input
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://..."
            data-testid="ops-briefing-url-input"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Calendar name</Label>
          <Input
            value={calendarName}
            onChange={e => setCalendarName(e.target.value)}
            placeholder="Sales → Ops Kickoff"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Host (optional)</Label>
          <Input
            value={hostName}
            onChange={e => setHostName(e.target.value)}
            placeholder="Matheus"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="rounded-full"
          disabled={link.saving}
          onClick={async () => {
            const ok = await link.save({
              booking_url: url.trim(),
              calendar_name: calendarName.trim(),
              host_name: hostName.trim(),
            });
            if (ok && !compact) setOpen(false);
          }}
          data-testid="ops-briefing-save-btn"
        >
          {link.saving ? "Saving..." : "Save"}
        </Button>
        {!compact && (
          <Button variant="ghost" size="sm" disabled={link.saving} onClick={() => setOpen(false)}>
            Cancel
          </Button>
        )}
      </div>
      {link.settings?.updated_at && (
        <p className="text-xs text-muted-foreground">
          Last updated by {link.settings.updated_by || "an admin"}
        </p>
      )}
    </div>
  );
}
